import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box, Button, CircularProgress, TextField
} from "@material-ui/core";
import SiteChrome from "../SiteChrome/SiteChrome";
import { checkBooking } from "../../Service";
import { launchCashfreeCheckout } from "../../Citizen/hooks/useCashfree";
import "./booking.css";

const PAYER_STORAGE_KEY = "pa_exit_payer";

function persistPayer(bookingId, name, email, mobile) {
  try {
    sessionStorage.setItem(
      PAYER_STORAGE_KEY,
      JSON.stringify({
        bookingId: String(bookingId || ""),
        name: name || "",
        email: email || "",
        mobile: mobile || ""
      })
    );
  } catch (_) {}
}

function readPayer(bookingId) {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(PAYER_STORAGE_KEY) || "null");
    if (parsed && String(parsed.bookingId) === String(bookingId)) return parsed;
  } catch (_) {}
  return null;
}

function buildCheckoutReturnUrl(bookingId) {
  return (
    window.location.origin +
    "/booking?bookingId=" +
    encodeURIComponent(bookingId) +
    "&orderId={order_id}"
  );
}

function qrImageUrl(data) {
  if (!data || !data.payload) return null;
  const p = data.payload;
  return (
    p.receipt_qrcode ||
    (p.charges && p.charges.payment_qrcode) ||
    null
  );
}

function paymentStatusLabel(status) {
  if (!status) return "";
  const s = String(status).toUpperCase();
  if (s === "COMPLETED" || s === "SUCCESS") return "Completed";
  if (s === "PENDING") return "Pending";
  if (s === "UNPAID") return "Unpaid";
  if (s === "FAILED") return "Failed";
  return status;
}

const RECEIPT_CHANNELS = ["SMS", "WHATSAPP", "WHASTAPP"];
const SETTLED_PAYMENT_TYPES = [
  "CASH", "CARD", "UPI", "PAYMENT_GATEWAY", "WALLET", "FASTAG", "RAZORPAY", "ONLINE"
];

function normType(type) {
  return String(type || "").trim().toUpperCase();
}

function isReceiptChannel(type) {
  return RECEIPT_CHANNELS.includes(normType(type));
}

function isSettledPaymentType(type) {
  return SETTLED_PAYMENT_TYPES.includes(normType(type));
}

function formatPaymentType(type) {
  if (!type || isReceiptChannel(type)) return "";
  const key = normType(type);
  const labels = {
    PAYMENT_GATEWAY: "Payment Gateway",
    UPI: "UPI",
    CASH: "Cash",
    CARD: "Card",
    WALLET: "Wallet",
    FASTAG: "FASTag",
    RAZORPAY: "Razorpay",
    ONLINE: "Online"
  };
  return labels[key] || type;
}

function formatCommunicationChannel(channel) {
  if (!channel) return "";
  const key = normType(channel);
  if (key === "SMS") return "SMS";
  if (key === "WHATSAPP" || key === "WHASTAPP") return "WhatsApp";
  return channel;
}

function isPaidPayload(payload) {
  if (!payload) return false;
  if (payload.payment_settled === true) return true;
  const status = normType(payload.payment_status);
  const ptype = normType(payload.payment_type);
  if (["COMPLETED", "SUCCESS"].includes(status) && isSettledPaymentType(ptype)) return true;
  if (
    payload.booking_exited &&
    payload.can_pay === false &&
    !isReceiptChannel(payload.communication_channel) &&
    !["PENDING", "UNPAID"].includes(status)
  ) {
    return true;
  }
  return false;
}

function canPayViaGateway(payload, paid) {
  if (!payload || paid) return false;
  if (payload.can_pay === true) return true;
  const status = normType(payload.payment_status);
  if (["COMPLETED", "SUCCESS"].includes(status) && isSettledPaymentType(payload.payment_type)) {
    return false;
  }
  const amount = Number(payload.charges?.charge_amount || 0);
  const inPlaza = payload.vehicle_in_plaza !== false;
  return inPlaza && amount > 0;
}

function hasAnprImages(payload) {
  if (!payload) return false;
  return !!(payload.anpr_plate_url || payload.anpr_frame_url);
}

function BookingAnprGallery({ payload }) {
  if (!hasAnprImages(payload)) return null;

  const plateText = payload.anpr_plate_text
    ? String(payload.anpr_plate_text).trim().toUpperCase()
    : "";
  const plateUrl = payload.anpr_plate_url || "";
  const frameUrl = payload.anpr_frame_url || "";

  return (
    <div className="pa-booking__anpr">
      {plateText && (
        <p className="pa-booking__anpr-plate-label">
          Detected plate: <strong>{plateText}</strong>
        </p>
      )}
      <div className="pa-booking__anpr-grid">
        {plateUrl && (
          <figure className="pa-booking__anpr-figure">
            <figcaption>Number plate</figcaption>
            <img
              src={plateUrl}
              alt={plateText ? `Plate ${plateText}` : "ANPR number plate"}
              className="pa-booking__anpr-img pa-booking__anpr-img--plate"
              loading="lazy"
            />
          </figure>
        )}
        {frameUrl && (
          <figure className="pa-booking__anpr-figure pa-booking__anpr-figure--frame">
            <figcaption>Vehicle</figcaption>
            <img
              src={frameUrl}
              alt="Vehicle at entry"
              className="pa-booking__anpr-img pa-booking__anpr-img--frame"
              loading="lazy"
            />
          </figure>
        )}
      </div>
    </div>
  );
}

function buildDetailRows(payload, bookingId, options) {
  if (!payload) return [];
  const opts = options || {};
  const showPayment = opts.showPayment !== false;
  const rows = [
    ["Booking Id", payload.bookingNo || bookingId],
    ["Vehicle No", payload.VRNumber],
    ["Vehicle Type", payload.vehicleType],
    ["Entry Time", payload.charges?.entryTime],
    ["Exit Time", payload.charges?.exitTime],
    ["User Type", payload.charges?.userType],
    ["Parking Plaza", payload.parkingName],
    ...(payload.charges?.charge_type
      ? [["Charge type", payload.charges.charge_type]]
      : []),
    ...(payload.anpr_plate_text &&
      payload.anpr_plate_text !== payload.VRNumber
      ? [["ANPR plate", String(payload.anpr_plate_text).toUpperCase()]]
      : [])
  ];

  if (showPayment) {
    const paymentStatus = paymentStatusLabel(payload.payment_status);
    const paymentType = formatPaymentType(payload.payment_type);
    if (paymentType) rows.push(["Payment type", paymentType]);
    if (paymentStatus) rows.push(["Payment status", paymentStatus]);
    if (payload.transaction_id) rows.push(["Transaction Id", payload.transaction_id]);
  }

  return rows.filter(function (r) { return r[1] != null && r[1] !== ""; });
}

function BookingPaymentSection({ payload, paid }) {
  const commChannel = formatCommunicationChannel(payload.communication_channel);
  const paymentType = formatPaymentType(payload.payment_type);
  const paymentStatus = paymentStatusLabel(payload.payment_status);
  const txnId = paid ? payload.transaction_id : "";

  if (!commChannel && !paymentType && !paymentStatus && !txnId) return null;

  return (
    <div className={"pa-booking__payment" + (paid ? " pa-booking__payment--done" : "")}>
      <h3 className="pa-booking__payment-title">{paid ? "Payment details" : "Payment status"}</h3>
      <div className="pa-booking__rows pa-booking__rows--payment">
        {commChannel && !paid && (
          <div className="pa-booking__row">
            <span className="pa-booking__row-k">Booking link sent via</span>
            <span className="pa-booking__row-v">{commChannel}</span>
          </div>
        )}
        {commChannel && !paid && (
          <div className="pa-booking__row pa-booking__row--hint">
            <span className="pa-booking__row-k">Note</span>
            <span className="pa-booking__row-v">
              {commChannel} is only how we shared this page. Payment is still pending.
            </span>
          </div>
        )}
        {paymentType && (
          <div className="pa-booking__row">
            <span className="pa-booking__row-k">Payment type</span>
            <span className="pa-booking__row-v">{paymentType}</span>
          </div>
        )}
        {paymentStatus && (
          <div className="pa-booking__row">
            <span className="pa-booking__row-k">Payment status</span>
            <span className={"pa-booking__row-v pa-booking__status pa-booking__status--" + String(payload.payment_status || "").toLowerCase()}>
              {paymentStatus}
            </span>
          </div>
        )}
        {txnId && (
          <div className="pa-booking__row pa-booking__row--txn">
            <span className="pa-booking__row-k">Transaction Id</span>
            <span className="pa-booking__row-v pa-booking__txn-id">{txnId}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * /booking — vehicle exit + pay flow for ParkAdda parking plazas.
 */
const Booking = function () {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const bookingId = searchParams.get("bookingId");
  const orderId = searchParams.get("orderId");

  const savedPayer = readPayer(bookingId);
  const [name, setName] = useState(savedPayer && savedPayer.name ? savedPayer.name : "");
  const [email, setEmail] = useState(savedPayer && savedPayer.email ? savedPayer.email : "");
  const [mobile, setMobile] = useState(savedPayer && savedPayer.mobile ? savedPayer.mobile : "");
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [paymentJustCompleted, setPaymentJustCompleted] = useState(false);

  const applyPaidResult = (chargePayload, payRes, paidOrderId) => {
    const merged = {
      ...(chargePayload && chargePayload.payload ? chargePayload.payload : {}),
      ...((payRes && payRes.data && payRes.data.payload) || {})
    };
    if (!merged.transaction_id && paidOrderId) {
      merged.transaction_id = paidOrderId;
    }
    if (!merged.payment_type) {
      merged.payment_type = "PAYMENT_GATEWAY";
    }
    setPaymentJustCompleted(true);
    setData({ status: "success", payload: merged });
  };

  const confirmGatewayPayment = (paidOrderId, chargePayload) => {
    const payer = readPayer(bookingId) || {};
    return checkBooking({
      bookingId: bookingId,
      operation: "getPaymentAndOut",
      name: name || payer.name,
      mobile: mobile || payer.mobile,
      email: email || payer.email,
      paymentReferenceId: paidOrderId
    }).then((res) => {
      setLoading(false);
      if (!res) return;
      if (res.data.status === "success") {
        applyPaidResult(chargePayload, res, paidOrderId);
      } else {
        alert(res.data.message || "Payment is not complete yet. Please retry.");
        if (chargePayload) setData(chargePayload);
      }
    }).catch(() => {
      setLoading(false);
      alert("Could not confirm payment. If you were charged, refresh this page.");
    });
  };

  useEffect(() => {
    if (!bookingId) return;

    if (orderId) {
      setLoading(true);
      let chargePayload = null;
      checkBooking({ bookingId: bookingId, operation: "getCharges" })
        .then((chargeRes) => {
          if (chargeRes.data.status === "success") {
            chargePayload = chargeRes.data;
            setData(chargeRes.data);
          }
          return confirmGatewayPayment(orderId, chargePayload);
        })
        .catch(() => { setLoading(false); });
      return;
    }

    setLoading(true);
    checkBooking({ bookingId: bookingId, operation: "getCharges" })
      .then((response) => {
        setLoading(false);
        if (response.data.status === "success") setData(response.data);
        else alert(response.data.message);
      })
      .catch(() => { setLoading(false); });
    // eslint-disable-next-line
  }, []);

  const handleInputChange = (type, value) => {
    if (type === "name") setName(value);
    else if (type === "email") setEmail(value);
    else setMobile(value);
  };

  const handlePayNowClick = () => {
    if (name === "" || name === null || name === undefined) { alert("Enter your name."); return; }
    if (email === "" || email === null || email === undefined) { alert("Enter your email address."); return; }
    if (mobile === "" || mobile === null || mobile === undefined) { alert("Enter your mobile no."); return; }
    if (String(mobile).length !== 10) { alert("Enter 10 digit mobile no."); return; }

    persistPayer(bookingId, name, email, mobile);
    setLoading(true);
    checkBooking({
      bookingId: bookingId,
      totalBill: data && data.payload.charges.charge_amount,
      operation: "getPGToken",
      name: name, mobile: mobile, email: email
    })
      .then((response) => {
        if (response.data.status !== "success") {
          setLoading(false);
          alert(response.data.message || "Could not start payment.");
          return null;
        }
        const payload = response.data.payload || {};
        if (payload.already_paid && payload.order_id) {
          return confirmGatewayPayment(payload.order_id, data);
        }
        if (!payload.token) {
          setLoading(false);
          alert("Payment session was not created. Please retry.");
          return null;
        }
        return launchCashfreeCheckout(payload.token, buildCheckoutReturnUrl(bookingId))
          .then(function (result) {
            if (result && result.error) {
              setLoading(false);
              alert(result.error.message || "Payment could not be started. Please retry.");
            }
          });
      })
      .catch((err) => {
        setLoading(false);
        alert((err && err.message) || "Could not start payment. Please retry.");
      });
  };

  const wrongVehicle = () => {
    alert("Kindly call us for rectification or drop mail to support@parkadda.com.");
  };

  const retry = () => {
    setLoading(true);
    setPaymentJustCompleted(false);
    checkBooking({ bookingId: bookingId, operation: "getCharges" })
      .then((response) => {
        setLoading(false);
        if (response.data.status === "success") setData(response.data);
        else alert(response.data.message);
      })
      .catch(() => { setLoading(false); });
  };

  const payload = data && data.payload ? data.payload : null;
  const paid = paymentJustCompleted || isPaidPayload(payload);
  const canPay = canPayViaGateway(payload, paid);
  const paymentPending = payload && ["PENDING", "UNPAID", "FAILED"].includes(
    String(payload.payment_status || "UNPAID").toUpperCase()
  ) && !paid;
  const rows = buildDetailRows(payload, bookingId, { showPayment: !paid });
  const qrUrl = qrImageUrl(data);
  const amount = payload?.charges?.charge_amount;
  const receiptLinkSent = payload && !!formatCommunicationChannel(payload.communication_channel);
  const showPaymentSection = paid || receiptLinkSent ||
    payload?.payment_status || (paid && payload?.transaction_id);

  return (
    <SiteChrome
      title="Parking Booking Details | ParkAdda"
      description="View ParkAdda parking booking details and complete vehicle exit."
      canonical="https://www.parkadda.com/booking"
      noIndex
      hideFooter
    >
      <section className="pa-banner" id="bookingTop">
        <div className="pa-banner__inner">
          <div className="pa-banner__eyebrow" data-aos="fade-up">Exit your parked vehicle</div>
          <h1 className="pa-banner__title" data-aos="fade-up" data-aos-delay="80">
            Parkadda <em>Booking Details</em>
          </h1>
          <p className="pa-banner__sub" data-aos="fade-up" data-aos-delay="160">
            (Pay &amp; Exit Your Parked In Vehicle.)
          </p>
        </div>
      </section>

      <section className="pa-section pa-booking">
        {loading ? (
          <Box className="pa-booking__loader"><CircularProgress /></Box>
        ) : payload ? (
          <div className="pa-booking__card" data-aos="fade-up">
            {paid && (
              <div className="pa-booking__success-banner">
                <div className="pa-booking__success-ic" aria-hidden="true">✓</div>
                <div>
                  <h3>Payment completed. Vehicle exited successfully.</h3>
                  <p>Thank you for using ParkAdda. Drive safe!</p>
                </div>
              </div>
            )}

            {!paid && receiptLinkSent && canPay && (
              <div className="pa-booking__info-banner">
                <p>
                  A payment link was sent on {formatCommunicationChannel(payload.communication_channel)}.
                  {" "}That is only a notification — please complete payment below to exit.
                </p>
              </div>
            )}

            {!paid && paymentPending && canPay && (
              <div className="pa-booking__warn-banner">
                <p>Payment is not complete. Pay below using Cash, Card, UPI, Wallet, or Payment Gateway to exit.</p>
              </div>
            )}

            {!paid && payload.booking_exited && !canPay && (
              <div className="pa-booking__info-banner">
                <p>This vehicle has already exited. Booking details are shown below.</p>
              </div>
            )}

            <div className="pa-booking__head">
              <h2>Booking Details</h2>
              {amount != null && (
                <span className="pa-booking__total">
                  <small>{paid ? "Amount" : "Total Bill"}</small>
                  <strong>₹ {amount}</strong>
                </span>
              )}
            </div>

            {rows.length > 0 && (
              <div className="pa-booking__rows">
                {rows.map(function (r) {
                  return (
                    <div key={r[0]} className="pa-booking__row">
                      <span className="pa-booking__row-k">{r[0]}</span>
                      <span className="pa-booking__row-v">{r[1]}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <BookingAnprGallery payload={payload} />

            {showPaymentSection && (
              <BookingPaymentSection payload={payload} paid={paid} />
            )}

            {paid && amount != null && (
              <p className="pa-booking__paid-amount">
                Amount paid: <strong>₹ {amount}</strong>
              </p>
            )}

            {qrUrl && !canPay && (
              <div className="pa-booking__qr">
                <p className="pa-booking__qr-label">
                  {paid
                    ? "Scan QR (same as on your receipt)"
                    : "Pay via UPI — scan to pay at exit"}
                </p>
                <img src={qrUrl} alt="Payment QR code" className="pa-booking__qr-img" />
              </div>
            )}

            {canPay && (
              <>
                <h3 className="pa-booking__pay-title">Pay via payment gateway</h3>
                <p className="pa-booking__pay-hint">
                  You will be redirected to Cashfree to complete payment. After success, your vehicle exit is processed automatically.
                </p>
                <div className="pa-booking__fields">
                  <div className="pa-booking__field">
                    <label>Full Name</label>
                    <TextField
                      placeholder="Enter your name"
                      variant="outlined" size="small" fullWidth
                      value={name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                    />
                  </div>
                  <div className="pa-booking__field">
                    <label>Mobile No</label>
                    <TextField
                      placeholder="Enter your mobile no"
                      type="number" variant="outlined" size="small" fullWidth
                      value={mobile}
                      onChange={(e) => handleInputChange("mobile", e.target.value)}
                    />
                  </div>
                  <div className="pa-booking__field">
                    <label>Email Address</label>
                    <TextField
                      placeholder="Enter your email"
                      type="email" variant="outlined" size="small" fullWidth
                      value={email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                    />
                  </div>
                </div>

                <div className="pa-booking__actions">
                  <Button type="button" variant="contained" color="primary" fullWidth size="large" onClick={handlePayNowClick}>
                    <b>Pay Now</b>
                  </Button>
                  <Button variant="outlined" color="primary" fullWidth size="large" onClick={wrongVehicle}>
                    <b>Not my booking?</b>
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="pa-booking__empty" data-aos="fade-up">
            <div className="pa-booking__empty-ic" aria-hidden="true">?</div>
            <h3>Booking Details Not Found.</h3>
            <p>kindly mail to support@parkadda.com.</p>
            <Button variant="contained" color="primary" onClick={retry}>
              <b>Retry</b>
            </Button>
          </div>
        )}
      </section>
    </SiteChrome>
  );
};

export default Booking;
